// Phone header status (29 Sep 2026): ONE small glass capsule beside ☰ that
// answers "is anything working?" — the chat's agent and its helpers together.
// Desktop keeps SessionStatusPill + HelpersChip in the topbar; on phones those
// are hidden by styles/mobile-glass.css and this capsule is shown instead.
//
//   idle, nothing to say     → nothing (just ☰)
//   agent starting / working → orb + "Starting" / "Working"
//   helpers working          → bot + count      (agent + helpers: orb Working · bot 2)
//   a helper needs you       → amber "Needs you"
//   a helper failed / error  → red "1 failed" / "Error"
//   just finished            → green check "Done" for 10 s, like the desktop pill
//   helpers finished         → quiet grey "✓ 2" while the desktop chip would show
//
// Tapping it opens the Helpers sheet whenever the chat has helpers.

import { useEffect, useState } from "react";
import { useSession } from "../store/session";
import { useHelpersSheet } from "../store/helpersSheet";
import { useTranscriptSheet } from "../lib/transcriptSheet";
import { helperCountsFromStatus, helpersChip } from "../lib/helperPresentation";
import { HelperBot, HelperOrb } from "./HelperFaces";
import { toastGoesToIsland } from "../lib/islandNotice";

const READY_WINDOW_MS = 10_000;

type Tone = "working" | "needs" | "failed" | "done" | "ready";

export default function MobileAgentStatus() {
  const companyId = useSession((s) => s.activeCompanyId);
  const status = useSession((s) => (companyId ? s.sessionStatusByCompany[companyId] : undefined));
  const openHelpers = useHelpersSheet((s) => s.open);
  const openTranscript = useTranscriptSheet((s) => s.open);
  const expanded = useHelpersSheet((s) => !!companyId && s.companyId === companyId);
  // Re-render each second so "Done" expires on time (same as the desktop pill).
  // A short notice (link copied, chat forked) takes the capsule for its 4 s (30 Sep 2026).
  const toast = useSession((s) => s.toast);
  const sidebarOpen = useSession((s) => s.sidebarOpen);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  if (!companyId) return null;
  if (toast && toastGoesToIsland(toast, true, !sidebarOpen)) {
    return (
      <span className={"mAgent mAgent--notice is-" + (toast.kind === "success" ? "ready" : "done")} role="status" aria-live="polite">
        <span className="mAgent__part">
          <span className="mAgent__check" aria-hidden="true" />
          <span key={toast.id} className="mAgent__word mAgent__notice wordIn">{toast.text}</span>
        </span>
      </span>
    );
  }

  const counts = helperCountsFromStatus(status);
  const chip = helpersChip(counts);
  const running = counts?.running ?? 0;

  let agent: { tone: Tone; word: string } | null = null;
  if (status?.state === "spawning") agent = { tone: "working", word: "Starting" };
  else if (status?.state === "killing") agent = { tone: "done", word: "Stopping" };
  else if (status?.waitingForSlot) agent = { tone: "working", word: "Waiting for a slot" };
  else if (status?.state === "errored") agent = { tone: "failed", word: "Error" };
  else if (status?.state === "running" && (status.busy === true || !!status.autoTurn)) agent = { tone: "working", word: "Working" };
  else if (status?.state === "running" && status.busy === false && !running
    && status.lastDoneAt && now - status.lastDoneAt < READY_WINDOW_MS) agent = { tone: "ready", word: "Done" };

  let helpers: { tone: Tone; text: string } | null = null;
  if (chip && counts) {
    if (chip.tone === "needs") helpers = { tone: "needs", text: counts.needsYou > 1 ? `${counts.needsYou} need you` : "Needs you" };
    else if (chip.tone === "failed") helpers = { tone: "failed", text: `${counts.failed} failed` };
    else if (chip.tone === "working") helpers = { tone: "working", text: String(running) };
    // "Needs you" carries how many, like the chip under the reply.
    else helpers = { tone: "done", text: String(counts.done + counts.stopped) };
  }
  if (!agent && !helpers) return null;

  // The capsule's colour follows whatever most needs attention.
  const rank: Tone[] = ["failed", "needs", "working", "ready", "done"];
  const tone = rank.find((t) => agent?.tone === t || helpers?.tone === t) ?? "done";
  const label = [agent?.word, chip?.label].filter(Boolean).join(" · ") || "Status";
  // Two taps in one capsule (30 Sep): the agent word opens the live transcript, the helpers
  // part opens Helpers.
  const agentPart = agent && (
    <button type="button" className="mAgent__part mAgent__btn" aria-haspopup="dialog"
      aria-label={agent.word + ". Show the transcript"} onClick={() => openTranscript("latest")}>
      {agent.tone === "working" ? <HelperOrb />
        : agent.tone === "ready" ? <span className="mAgent__check" aria-hidden="true" />
        : <span className="mAgent__dot" aria-hidden="true" />}
      {/* Beside helpers on a very narrow phone the word folds away (CSS). */}
      <span key={agent.word} className="mAgent__word mAgent__agentWord wordIn">{agent.word}</span>
    </button>
  );
  const helpersInner = helpers && (
    <>
      {helpers.tone === "working"
        ? <HelperBot bot={counts?.faces?.[0]} id={companyId} state="running" size={20} />
        : helpers.tone === "done"
        ? <span className="mAgent__check" aria-hidden="true" />
        : <span className="mAgent__dot" aria-hidden="true" />}
      <span className="mAgent__word">{helpers.text}</span>
    </>
  );
  const helpersPart = helpers && (chip
    ? <button type="button" className={"mAgent__part mAgent__helpers mAgent__btn is-" + helpers.tone}
        aria-label={/show helpers/i.test(chip?.label ?? "") ? chip!.label : (chip?.label ?? "Helpers") + ". Show helpers"} aria-haspopup="dialog" aria-expanded={expanded}
        onClick={(event) => openHelpers(companyId, event.currentTarget)}>{helpersInner}</button>
    : <span className={"mAgent__part mAgent__helpers is-" + helpers.tone}>{helpersInner}</span>);

  return (
    <span className={"mAgent is-" + tone + (agent && helpers ? " has-both" : "")} role="group" aria-label={label}>
      {agentPart}
      {helpersPart}
    </span>
  );
}
